// Shows the predicted adult height for the active baby, derived from the
// biological parents' heights via the TNO formula. Surfaces a friendly empty
// state when sex / parent heights are missing.

import { Link } from 'react-router';
import { growth } from '@shared/index.ts';
import type { Baby, FamilyMember } from '@shared/types.ts';
import { AnimatedNumber } from '../AnimatedNumber.tsx';

type Props = {
  baby: Baby;
  members: FamilyMember[];
};

export function TargetHeightCard({ baby, members }: Props) {
  const father = members.find((m) => m.relationship === 'biological_father');
  const mother = members.find((m) => m.relationship === 'biological_mother');
  const fatherCm = father?.height_mm != null ? father.height_mm / 10 : null;
  const motherCm = mother?.height_mm != null ? mother.height_mm / 10 : null;

  if (!baby.sex) {
    return (
      <EmptyCard
        title="Target adult height"
        body="Add Lion's sex on the Family screen to compute the prediction."
      />
    );
  }
  if (fatherCm == null || motherCm == null) {
    const missing = [
      fatherCm == null ? 'biological father' : null,
      motherCm == null ? 'biological mother' : null,
    ]
      .filter(Boolean)
      .join(' and ');
    return (
      <EmptyCard
        title="Target adult height"
        body={`Set the height of the ${missing} on the Family screen to estimate ${baby.name}'s adult height.`}
        cta={
          <Link
            to="/family"
            style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 500, textDecoration: 'none' }}
          >
            Open Family →
          </Link>
        }
      />
    );
  }

  const { th_cm, range_cm } = growth.tnoTargetHeight(baby.sex, fatherCm, motherCm);
  const lo = th_cm - range_cm;
  const hi = th_cm + range_cm;

  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 18,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.04,
          }}
        >
          Target adult height
        </div>
        <AnimatedNumber
          value={th_cm}
          format={(v) => `${v.toFixed(0)} cm`}
          className="lt-serif lt-tabular"
          style={{
            fontSize: 28,
            fontWeight: 400,
            letterSpacing: -0.015,
            marginTop: 4,
            display: 'inline-block',
          }}
        />
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
          95% range {lo.toFixed(0)}–{hi.toFixed(0)} cm
        </div>
      </div>
      <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
        <div>father {fatherCm.toFixed(0)} cm</div>
        <div>mother {motherCm.toFixed(0)} cm</div>
      </div>
    </div>
  );
}

function EmptyCard({ title, body, cta }: { title: string; body: string; cta?: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 18,
        background: 'var(--bg-card)',
        border: '1px dashed var(--border-strong)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.4 }}>
        {body}
      </div>
      {cta && <div style={{ marginTop: 8 }}>{cta}</div>}
    </div>
  );
}
